import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Dropdown, Button, Dimmer, Loader, Progress } from 'semantic-ui-react'
import { admin, api } from 'utils/actions'
import { cards, isVideoCard } from '../components/Home'
import { uploadVideo, hasYoutubeConfig } from 'utils/youtube'
import { uploadRaw } from 'utils/cloudinary'

// Folder options come from the Home page cards. The first entries are YouTube
// videos, shown as "Video 1", "Video 2". A non-video card at index i maps to
// the Cloudinary folder "slider-{i}".
// "Banner" is the home page top slider, which lives in the "slider" folder.
const folderOptions = [{ text: 'Banner', value: 'banner' }].concat(
  cards.map((c, i) => {
    if (isVideoCard(c)) {
      const n = cards.slice(0, i + 1).filter(isVideoCard).length
      return { text: `Video ${n}`, value: i }
    }
    return { text: c, value: i }
  })
)

// 0-based index of a video card among the video cards
const videoOrdinal = i => cards.slice(0, i + 1).filter(isVideoCard).length - 1

// Cloudinary folder for a selected dropdown value: 'banner' -> the home top
// slider ("slider"); a card index i -> "slider-{i}".
const toCloudFolder = f => (f === 'banner' ? 'slider' : 'slider-' + f)

const isImg = f =>
  f.type === 'image/png' ||
  f.type === 'image/jpeg' ||
  /\.(png|jpe?g)$/i.test(f.name)

// Documents are only allowed for the banner; each page becomes an image.
const isDoc = f =>
  f.type === 'application/pdf' || /\.(pdf|docx?)$/i.test(f.name)


// Build a Cloudinary delivery URL (optionally with a transformation) straight
// from a resource returned by the admin API.
const cdImg = (r, t) =>
  `https://res.cloudinary.com/vttc/image/upload/${t ? t + '/' : ''}v${
    r.version
  }/${r.public_id}.${r.format || 'png'}`

// Trailing number of a public_id, e.g. "slider-2/5" -> 5
const imgNum = pid => +pid.slice(pid.lastIndexOf('/') + 1) || 0

const post = (qs, body) =>
  fetch(admin + qs, {
    method: 'post',
    headers: {
      Authorization: localStorage.getItem('token'),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body || {}),
  }).then(r => r.json())

const readDataUrl = file =>
  new Promise((resolve, reject) => {
    const fr = new FileReader()
    fr.onload = () => resolve(fr.result)
    fr.onerror = reject
    fr.readAsDataURL(file)
  })

const Pictures = () => {
  const [folder, setFolder] = useState(null) // selected card index
  const [images, setImages] = useState([])
  const [loading, setLoading] = useState(false) // listing (inline)
  const [busy, setBusy] = useState(false) // delete (blocking)
  const [uploading, setUploading] = useState(false) // upload (blocking)
  const [progress, setProgress] = useState(null) // { done, total, percent, label }
  const [dragOver, setDragOver] = useState(false)
  const [videos, setVideos] = useState([]) // current YouTube video ids
  const videoInput = useRef(null)
  const picInput = useRef(null)

  const isVideo = folder != null && isVideoCard(cards[folder])
  const cloudFolder = folder == null ? null : toCloudFolder(folder)
  const isBanner = folder === 'banner'
  const folderLabel = isBanner ? 'Banner' : cards[folder]
  const showStep2 = folder != null && !isVideo
  const blocked = busy || uploading
  const videoIdx = isVideo ? videoOrdinal(folder) : -1
  const currentVideoId = videoIdx >= 0 ? videos[videoIdx] : null

  useEffect(() => {
    fetch(api + 'lookup=1')
      .then(r => r.json())
      .then(d => setVideos((d && d.videos) || []))
      .catch(() => {})
  }, [])

  const loadImages = useCallback(async () => {
    if (folder == null || isVideoCard(cards[folder])) {
      setImages([])
      return
    }
    setLoading(true)
    const r = await post('cdfolder=1&folder=' + toCloudFolder(folder))
    setImages(Array.isArray(r) ? r : [])
    setLoading(false)
  }, [folder])

  useEffect(() => {
    loadImages()
  }, [loadImages])

  const deleteOne = async pid => {
    if (blocked || !window.confirm('Delete this image?')) return
    setBusy(true)
    await post('cddelete=1', { public_id: pid })
    // renumber the remaining images to a gapless 1..n sequence
    const list = await post('cdrenumber=1&folder=' + cloudFolder)
    setImages(Array.isArray(list) ? list : [])
    setBusy(false)
  }

  const deleteAll = async () => {
    if (blocked || !images.length) return
    if (!window.confirm('Delete ALL images in this folder? This cannot be undone.'))
      return
    setBusy(true)
    await post('cddeleteall=1', { folder: cloudFolder })
    setBusy(false)
    loadImages()
  }

  const onDrop = e => {
    e.preventDefault()
    setDragOver(false)
    uploadFiles(Array.from(e.dataTransfer.files || []))
  }

  const onSelectPictures = e => {
    const files = Array.from(e.target.files || [])
    e.target.value = '' // allow re-selecting the same files later
    uploadFiles(files)
  }

  const uploadFiles = async dropped => {
    if (blocked || !cloudFolder) return
    const isBanner = folder === 'banner'

    // Images always allowed; documents (converted page-by-page) only for banner.
    const accepted = dropped.filter(f => isImg(f) || (isBanner && isDoc(f)))
    const skipped = dropped.length - accepted.length
    if (skipped > 0)
      alert(
        `${skipped} file(s) skipped — only ${
          isBanner ? 'PNG, JPG/JPEG, PDF, DOC, DOCX' : 'PNG and JPG/JPEG'
        } are allowed.`
      )
    if (!accepted.length) return

    // Images first (client-numbered), then docs (server numbers after those).
    const files = accepted
      .filter(f => !isDoc(f))
      .concat(accepted.filter(isDoc))

    setUploading(true)
    setProgress({ done: 0, total: files.length, label: 'Uploading…' })
    let n = images.reduce((m, r) => Math.max(m, imgNum(r.public_id)), 0)
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      if (isDoc(file)) {
        setProgress({
          done: i,
          total: files.length,
          label: 'Converting document…',
        })
        const url = await uploadRaw(file)
        await post('cddoc=1&folder=' + cloudFolder, { url, filename: file.name })
      } else {
        const url = await readDataUrl(file)
        n += 1
        await post('cdupload=1', { url, folder: cloudFolder, name: n })
      }
      setProgress({ done: i + 1, total: files.length, label: 'Uploading…' })
    }
    // renumber so names stay gapless (also fixes any pre-existing gaps)
    const list = await post('cdrenumber=1&folder=' + cloudFolder)
    setImages(Array.isArray(list) ? list : [])
    setUploading(false)
    setProgress(null)
  }

  const onSelectVideo = e => {
    const file = e.target.files && e.target.files[0]
    e.target.value = '' // allow re-selecting the same file later
    if (file) uploadVideoFile(file)
  }

  const uploadVideoFile = async file => {
    if (blocked || videoIdx < 0) return
    if (!(file.type === 'video/mp4' || /\.mp4$/i.test(file.name))) {
      alert('Please select a single .mp4 file.')
      return
    }
    if (!hasYoutubeConfig()) {
      alert('YouTube upload is not configured (REACT_APP_GOOGLE_CLIENT_ID).')
      return
    }

    setUploading(true)
    const setPct = pct =>
      setProgress({
        done: pct,
        total: 100,
        percent: true,
        label: 'Uploading to YouTube…',
      })
    setPct(0)
    try {
      const videoId = await uploadVideo(file, {
        title: (folderOptions[folder] || {}).text || 'VTTC Video',
        privacyStatus: 'unlisted',
        onProgress: (loaded, total) => setPct(Math.round((loaded / total) * 100)),
      })
      const list = await post('setvideo=1', { index: videoIdx, videoId })
      setVideos(Array.isArray(list) ? list : [])
    } catch (err) {
      alert('Upload failed: ' + err.message)
    }
    setUploading(false)
    setProgress(null)
  }

  return (
    <div>
      <h3>Pictures</h3>

      <div className="mt8">
        Step 1 - Select a folder:{' '}
        <Dropdown
          selection
          options={folderOptions}
          value={folder == null ? '' : folder}
          placeholder="Select a folder"
          style={{ width: '150px' }}
          onChange={(e, x) => setFolder(x.value)}
        />
      </div>

      {isVideo && (
        <div className="mt16">
          <h4 style={{ margin: 0 }}>
            {(folderOptions[folder] || {}).text} — current video
          </h4>
          <div className="mt8">
            {currentVideoId ? (
              <iframe
                title="current-video"
                width="400"
                height="225"
                src={`https://www.youtube.com/embed/${currentVideoId}`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div style={{ color: '#888' }}>No video set yet.</div>
            )}
          </div>

          {!hasYoutubeConfig() && (
            <div className="mt8" style={{ color: '#c00' }}>
              YouTube upload not configured — set REACT_APP_GOOGLE_CLIENT_ID in
              .env.
            </div>
          )}

          <div className="mt8">
            <input
              ref={videoInput}
              type="file"
              accept="video/mp4,.mp4"
              hidden
              onChange={onSelectVideo}
            />
            <Button
              primary
              icon="upload"
              content="Upload Video"
              disabled={blocked || !hasYoutubeConfig()}
              onClick={() => videoInput.current && videoInput.current.click()}
            />
            <span style={{ color: '#888', marginLeft: 8 }}>
              Select a single .mp4 — it is uploaded to the @vttclive YouTube
              channel (unlisted) and set as this video.
            </span>
          </div>
        </div>
      )}

      {showStep2 && (
        <div className="mt16">
          <div
            className="f"
            style={{ justifyContent: 'space-between', alignItems: 'center' }}
          >
            <h4 style={{ margin: 0 }}>
              Step 2 - Images in "{folderLabel}" ({images.length})
            </h4>
            <div>
              <input
                ref={picInput}
                type="file"
                accept={
                  'image/png,image/jpeg,.png,.jpg,.jpeg' +
                  (isBanner ? ',application/pdf,.pdf,.doc,.docx' : '')
                }
                multiple
                hidden
                onChange={onSelectPictures}
              />
              <Button
                primary
                size="small"
                icon="upload"
                content="Upload Pictures"
                disabled={blocked}
                onClick={() => picInput.current && picInput.current.click()}
              />
              <Button
                color="red"
                size="small"
                disabled={!images.length}
                onClick={deleteAll}
              >
                Delete All
              </Button>
            </div>
          </div>

          <div
            onDragOver={e => {
              e.preventDefault()
              setDragOver(true)
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className="mt8"
            style={{
              border: `2px dashed ${dragOver ? '#2185d0' : '#ccc'}`,
              borderRadius: 6,
              padding: 12,
              background: dragOver ? '#f0f8ff' : '#fafafa',
              minHeight: 170,
            }}
          >
            <div className="tac" style={{ color: '#888', marginBottom: 8 }}>
              {isBanner
                ? 'Drag & drop PNG, JPG/JPEG, or PDF/DOC/DOCX here'
                : 'Drag & drop PNG or JPG/JPEG here to upload'}
            </div>

            {loading ? (
              <div className="ui active inline centered loader" />
            ) : images.length ? (
              <div className="f" style={{ flexWrap: 'wrap', gap: 12 }}>
                {images.map(r => (
                  <div
                    key={r.public_id}
                    style={{ position: 'relative', width: 160 }}
                  >
                    <a
                      href={cdImg(r)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={cdImg(r, 'c_fit,w_320,h_240')}
                        alt=""
                        style={{
                          width: '100%',
                          height: 120,
                          objectFit: 'cover',
                          borderRadius: 4,
                          border: '1px solid #ddd',
                        }}
                      />
                    </a>
                    <Button
                      icon="trash"
                      color="red"
                      size="mini"
                      circular
                      onClick={() => deleteOne(r.public_id)}
                      style={{ position: 'absolute', top: 4, right: 4 }}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="tac" style={{ color: '#bbb' }}>
                No images yet.
              </div>
            )}
          </div>
        </div>
      )}

      <Dimmer active={blocked} page>
        {progress ? (
          <div style={{ width: 320 }}>
            {progress.percent ? (
              <Progress percent={progress.done} progress indicating inverted>
                {progress.label}
              </Progress>
            ) : (
              <Progress
                value={progress.done}
                total={progress.total}
                progress="ratio"
                indicating
                inverted
              >
                {progress.label}
              </Progress>
            )}
          </div>
        ) : (
          <Loader>Working...</Loader>
        )}
      </Dimmer>
    </div>
  )
}

export default Pictures
